import { ReactElement, useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { Button } from '../../../components/Button/Button';
import classes from './LeadListPage.module.css';
import { Alert } from '../../../components/Alert/Alert';
import { useLeads, usePipelineStats, type LeadStatus } from '../../../hooks/leads/useLeads';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { LeadTable } from '../../../components/LeadTable/LeadTable';
import { Routes } from '../../../enums/Routes';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { StatsCardsGrid, type StatsCardConfig } from '../../../components/StatsCard/StatsCard';
import { UpcomingFollowUps } from '../../../components/UpcomingFollowUps/UpcomingFollowUps';
import { QualityPipelineCard } from '../../../components/QualityPipelineCard/QualityPipelineCard';
import { BriefcaseIcon, WalletIcon, MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon, FunnelIcon } from '@navikt/aksel-icons';
import { formatNOK } from '../../../utils/currencyFormatters';
import { LEAD_STATUS_LABELS } from '../../../constants/lead-status';

const PAGE_TITLE = 'Leads';
const ERROR_MESSAGE = 'Kunne ikke laste leads. Prøv igjen senere.';
const ADD_LEAD_BUTTON = 'Legg til ny lead';

const createStatsCards = (
  totalLeads: number,
  totalMonthlyCashback: number,
  totalYearlyCashback: number,
  conversionRate: number | null,
  lostOpportunitiesCount: number,
  lostMonthlyCashback: number
): readonly StatsCardConfig[] => [
  {
    title: 'Aktive leads',
    icon: <BriefcaseIcon />,
    value: totalLeads,
    subtitle: 'I pipeline',
  },
  {
    title: 'Pipeline månedlig cashback',
    icon: <WalletIcon />,
    value: formatNOK(totalMonthlyCashback),
    subtitle: 'Estimert',
  },
  {
    title: 'Pipeline årlig cashback',
    icon: <WalletIcon />,
    value: formatNOK(totalYearlyCashback),
    subtitle: 'Estimert',
  },
  {
    title: 'Tapte muligheter',
    icon: <BriefcaseIcon />,
    value: lostOpportunitiesCount,
    subtitle: lostOpportunitiesCount > 0
      ? `${formatNOK(lostMonthlyCashback)}/mnd tapt`
      : conversionRate !== null ? `Conv. rate: ${conversionRate.toFixed(1)}%` : undefined,
  },
];

// Active statuses (excluding completed/lost)
const ACTIVE_STATUSES: LeadStatus[] = ['NEW', 'CONTACTED', 'QUALIFIED', 'PROPOSAL_SENT', 'NEGOTIATING'];

export const LeadListPage = (): ReactElement => {
  const navigate = useNavigate();
  const { data: leads, isLoading, error } = useLeads();
  const { data: pipelineStats } = usePipelineStats();

  // Filter state - default to showing only active statuses
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<Set<LeadStatus>>(
    new Set(ACTIVE_STATUSES)
  );
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleCreateLead = () => {
    navigate(Routes.PartnerAdminCreateLead);
  };

  const toggleStatus = (status: LeadStatus) => {
    setSelectedStatuses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const removeStatus = (status: LeadStatus) => {
    setSelectedStatuses((prev) => {
      const newSet = new Set(prev);
      newSet.delete(status);
      return newSet;
    });
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedStatuses(new Set(ACTIVE_STATUSES));
  };

  const showAllStatuses = () => {
    setSelectedStatuses(new Set(Object.keys(LEAD_STATUS_LABELS) as LeadStatus[]));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredLeads = useMemo(() => {
    if (!leads) return [];

    let filtered = leads.filter((lead) => lead.convertedToTenantId === null);

    // Apply status filter
    filtered = filtered.filter((lead) => selectedStatuses.has(lead.status));

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((lead) =>
        lead.companyName.toLowerCase().includes(query) ||
        lead.contactPersonName?.toLowerCase().includes(query) ||
        lead.contactEmail?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [leads, selectedStatuses, searchQuery]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchQuery.trim()) count++;
    if (selectedStatuses.size !== ACTIVE_STATUSES.length ||
        ![...selectedStatuses].every(s => ACTIVE_STATUSES.includes(s))) count++;
    return count;
  }, [searchQuery, selectedStatuses]);

  const statsCards = useMemo(() => {
    if (!pipelineStats) {
      return createStatsCards(0, 0, 0, null, 0, 0);
    }
    return createStatsCards(
      pipelineStats.totalLeads,
      pipelineStats.totalEstimatedMonthlyCashbackNOK,
      pipelineStats.totalEstimatedYearlyCashbackNOK,
      pipelineStats.conversionRate,
      pipelineStats.lostOpportunitiesCount,
      pipelineStats.lostOpportunitiesMonthlyCashbackNOK
    );
  }, [pipelineStats]);

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <div className={classes.headerContent}>
          <div>
            <Heading as="h1">{PAGE_TITLE}</Heading>
            <Paragraph size="lg">
              Administrer potensielle kunder i din salgspipeline
            </Paragraph>
          </div>
          <Button variant="primary" size="medium" onClick={handleCreateLead}>
            {ADD_LEAD_BUTTON}
          </Button>
        </div>
      </header>

      <div className={classes.content}>
        {isLoading && <GhostLoader />}

        {error && (
          <Alert variant="danger" size="md">
            <Paragraph>{ERROR_MESSAGE}</Paragraph>
          </Alert>
        )}

        {leads && !isLoading && (
          <>
            <StatsCardsGrid cards={statsCards} columns={4} />

            {(pipelineStats?.byQuality || pipelineStats?.upcomingFollowUps) && (
              <div className={classes.sideCardsGrid}>
                {pipelineStats?.byQuality && (
                  <QualityPipelineCard byQuality={pipelineStats.byQuality} />
                )}
                {pipelineStats?.upcomingFollowUps && (
                  <UpcomingFollowUps followUps={pipelineStats.upcomingFollowUps} />
                )}
              </div>
            )}

            <ContainerCard>
              <ContainerCard.Title>
                <div className={classes.tableHeader}>
                  <span>Alle leads ({filteredLeads.length})</span>
                  {activeFilterCount > 0 && (
                    <button className={classes.clearAllBtn} onClick={clearAllFilters}>
                      <XMarkIcon className={classes.clearIcon} />
                      Fjern alle filtre
                    </button>
                  )}
                </div>
              </ContainerCard.Title>

              {/* Filter Bar */}
              <div className={classes.filterBar}>
                {/* Search Field */}
                <div className={classes.searchField}>
                  <MagnifyingGlassIcon className={classes.searchIcon} />
                  <input
                    type="text"
                    placeholder="Søk i firmanavn, kontaktperson eller e-post..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={classes.searchInput}
                  />
                  {searchQuery && (
                    <button
                      className={classes.clearSearchBtn}
                      onClick={() => setSearchQuery('')}
                      aria-label="Fjern søk"
                    >
                      <XMarkIcon />
                    </button>
                  )}
                </div>

                {/* Status Multi-Select Dropdown */}
                <div className={classes.dropdownContainer} ref={dropdownRef}>
                  <button
                    className={classes.dropdownButton}
                    onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                  >
                    <FunnelIcon className={classes.dropdownIcon} />
                    Status
                    {selectedStatuses.size !== Object.keys(LEAD_STATUS_LABELS).length && (
                      <span className={classes.filterBadge}>{selectedStatuses.size}</span>
                    )}
                    <ChevronDownIcon className={classes.chevronIcon} />
                  </button>

                  {isStatusDropdownOpen && (
                    <div className={classes.dropdownMenu}>
                      <div className={classes.dropdownHeader}>
                        <span className={classes.dropdownTitle}>Velg statuser</span>
                        <button
                          className={classes.selectAllBtn}
                          onClick={showAllStatuses}
                        >
                          Velg alle
                        </button>
                      </div>
                      <div className={classes.dropdownOptions}>
                        {(Object.entries(LEAD_STATUS_LABELS) as [LeadStatus, string][]).map(([status, label]) => (
                          <label key={status} className={classes.checkboxOption}>
                            <input
                              type="checkbox"
                              checked={selectedStatuses.has(status)}
                              onChange={() => toggleStatus(status)}
                              className={classes.checkbox}
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Filter Buttons */}
                <div className={classes.quickFilters}>
                  <button
                    className={`${classes.quickFilterBtn} ${
                      selectedStatuses.size === ACTIVE_STATUSES.length &&
                      [...selectedStatuses].every(s => ACTIVE_STATUSES.includes(s))
                        ? classes.active
                        : ''
                    }`}
                    onClick={() => setSelectedStatuses(new Set(ACTIVE_STATUSES))}
                  >
                    Kun aktive
                  </button>
                  <button
                    className={`${classes.quickFilterBtn} ${
                      selectedStatuses.size === Object.keys(LEAD_STATUS_LABELS).length
                        ? classes.active
                        : ''
                    }`}
                    onClick={showAllStatuses}
                  >
                    Alle
                  </button>
                </div>
              </div>

              {/* Applied Filter Chips */}
              {(searchQuery || selectedStatuses.size !== ACTIVE_STATUSES.length ||
                ![...selectedStatuses].every(s => ACTIVE_STATUSES.includes(s))) && (
                <div className={classes.appliedFilters}>
                  <span className={classes.appliedFiltersLabel}>Aktive filtre:</span>
                  {searchQuery && (
                    <div className={classes.filterChip}>
                      <span>Søk: "{searchQuery}"</span>
                      <button
                        className={classes.chipRemoveBtn}
                        onClick={() => setSearchQuery('')}
                        aria-label="Fjern søkefilter"
                      >
                        <XMarkIcon />
                      </button>
                    </div>
                  )}
                  {[...selectedStatuses].map((status) => (
                    <div key={status} className={classes.filterChip}>
                      <span>{LEAD_STATUS_LABELS[status]}</span>
                      <button
                        className={classes.chipRemoveBtn}
                        onClick={() => removeStatus(status)}
                        aria-label={`Fjern ${LEAD_STATUS_LABELS[status]} filter`}
                      >
                        <XMarkIcon />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <ContainerCard.Body noPadding>
                {filteredLeads.length > 0 ? (
                  <LeadTable leads={filteredLeads} />
                ) : (
                  <div className={classes.emptyState}>
                    <Paragraph size="lg">
                      {selectedStatuses.size === 0
                        ? 'Velg minst én status for å vise leads'
                        : 'Ingen leads funnet med valgte filtre'}
                    </Paragraph>
                    {selectedStatuses.size > 0 && (
                      <Paragraph size="sm" className={classes.emptyStateDescription}>
                        Prøv å justere filtrene eller legg til en ny lead
                      </Paragraph>
                    )}
                    <Button variant="primary" size="medium" onClick={handleCreateLead}>
                      {ADD_LEAD_BUTTON}
                    </Button>
                  </div>
                )}
              </ContainerCard.Body>
            </ContainerCard>
          </>
        )}
      </div>
    </div>
  );
};
